class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this._cache = {};   // query string → raw HTML string
    this._ac = null;    // AbortController for in-flight request
    this._dt = null;    // debounce timer
    this._si = -1;      // selected option index (-1 = none)
  }

  connectedCallback() {
    var s = this;
    var q = s.querySelector.bind(s);
    var on = function (el, ev, fn) { if (el) el.addEventListener(ev, fn); };

    s.inp  = q('[data-ps-input]');
    s.dd   = q('[data-ps-dropdown]');
    s.frm  = q('[data-ps-form]');
    s.ov   = q('[data-ps-overlay]');
    s.mf   = q('[data-ps-mobile]');
    s.mi   = q('[data-ps-mobile-input]');

    if (!s.inp || !s.dd) return;

    s._sid = s.dataset.sectionId || 'predictive-search';

    /* Persistent aria-live region for screen reader result announcements.
       The dropdown innerHTML is replaced on each result, which doesn't reliably
       trigger announcements — a separate region outside the replaced content does. */
    s._live = document.createElement('div');
    s._live.className = 'visually-hidden';
    s._live.setAttribute('role', 'status');
    s._live.setAttribute('aria-live', 'polite');
    s._live.setAttribute('aria-atomic', 'true');
    s.appendChild(s._live);

    /* Desktop input */
    on(s.inp, 'input',   function () { s._debounce(s.inp.value.trim()); });
    on(s.inp, 'keydown', function (e) { s._key(e); });
    on(s.inp, 'focus',   function () {
      if (s.inp.value.trim() && s.dd.innerHTML.trim()) s._open();
    });

    /* Mobile input (inside full-screen overlay) */
    on(s.mi, 'input',   function () { s._debounce(s.mi.value.trim()); });
    on(s.mi, 'keydown', function (e) { s._key(e); });

    /* Search icon / toggle */
    on(q('[data-ps-toggle]'), 'click', function (e) {
      e.preventDefault();
      if (s._isMobile()) {
        s._openMobile();
      } else {
        if (s.frm) s.frm.classList.toggle('is-open');
        s.inp.focus();
      }
    });

    /* Mobile overlay close button */
    on(q('[data-ps-mobile-close]'), 'click', function () { s._closeMobile(); });
    on(s.ov, 'click', function () { s._closeMobile(); });

    /* Prevent empty form submission */
    on(s.frm, 'submit', function (e) {
      if (!s._activeInput().value.trim()) e.preventDefault();
    });

    /* Close dropdown when clicking outside */
    document.addEventListener('click', function (e) {
      if (!s.contains(e.target)) s._closeDropdown();
    });
  }

  _isMobile() { return window.innerWidth <= 768; }

  _activeInput() { return this._isMobile() && this.mi ? this.mi : this.inp; }

  _openMobile() {
    if (this.ov) this.ov.classList.add('is-visible');
    if (this.mf) this.mf.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    if (this.mi) this.mi.focus();
  }

  _closeMobile() {
    if (this.ov) this.ov.classList.remove('is-visible');
    if (this.mf) this.mf.classList.remove('is-visible');
    document.body.style.overflow = '';
    this._closeDropdown();
    if (this.mi) this.mi.value = '';
  }

  /* Keyboard navigation: arrows cycle options, Enter follows link, Escape closes */
  _key(e) {
    var options = this.dd.querySelectorAll('[role="option"]');
    var key = e.key;

    if (key === 'ArrowDown') {
      e.preventDefault();
      this._si = Math.min(this._si + 1, options.length - 1);
      this._setActive(options);
    } else if (key === 'ArrowUp') {
      e.preventDefault();
      this._si = Math.max(this._si - 1, -1);
      this._setActive(options);
    } else if (key === 'Enter' && this._si >= 0 && options[this._si]) {
      e.preventDefault();
      options[this._si].click();
    } else if (key === 'Escape') {
      this._closeDropdown();
      this._activeInput().focus();
    }
  }

  /* Update aria-selected and aria-activedescendant for keyboard-focused option */
  _setActive(options) {
    var idx = this._si;
    var active = this._activeInput();
    options.forEach(function (opt, i) {
      opt.setAttribute('aria-selected', i === idx ? 'true' : 'false');
    });
    if (idx >= 0 && options[idx]) {
      active.setAttribute('aria-activedescendant', options[idx].id);
      options[idx].scrollIntoView({ block: 'nearest' });
    } else {
      active.removeAttribute('aria-activedescendant');
    }
  }

  /* Debounce input — fires _fetch after 300ms of silence */
  _debounce(query) {
    clearTimeout(this._dt);
    var s = this;
    this._dt = setTimeout(function () { s._fetch(query); }, 300);
  }

  _fetch(query) {
    if (!query) {
      this._closeDropdown();
      this.dd.innerHTML = '';
      this._announce('');
      return;
    }

    /* Return cached result immediately without a network request */
    if (this._cache[query]) {
      this._render(this._cache[query]);
      return;
    }

    /* Cancel any in-flight request */
    if (this._ac) this._ac.abort();
    this._ac = new AbortController();

    this.dd.setAttribute('data-loading', 'true');
    this._open();

    var s = this;
    /* Use Shopify.routes.root (not Shopify.locale) — standard Dawn pattern */
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root + 'search/suggest'
      + '?q=' + encodeURIComponent(query)
      + '&section_id=' + s._sid
      + '&resources[type]=product,collection,page'
      + '&resources[limit]=4';

    fetch(url, { signal: s._ac.signal })
      .then(function (res) {
        if (!res.ok) throw res;
        return res.text();
      })
      .then(function (html) {
        s._cache[query] = html;
        s._render(html);
      })
      .catch(function (err) {
        if (err.name !== 'AbortError') {
          /* Close dropdown on real errors so stale/loading state isn't shown */
          s._closeDropdown();
        }
      })
      .finally(function () {
        s.dd.removeAttribute('data-loading');
      });
  }

  /* Parse section HTML returned by Shopify, inject into dropdown */
  _render(html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var results = parsed.querySelector('#predictive-search-results');
    this.dd.innerHTML = results ? results.innerHTML : html;
    this._si = -1;
    this._open();

    /* Announce result count via persistent live region */
    var count = this.dd.querySelectorAll('[role="option"]').length;
    if (count === 1) {
      this._announce(this.dataset.i18nOneResult || '1 result found');
    } else if (count > 1) {
      this._announce(
        (this.dataset.i18nResultsCount || '{{ count }} results found').replace('{{ count }}', count)
      );
    } else {
      this._announce(this.dataset.i18nNoResults || 'No results found');
    }
  }

  _open() {
    this.dd.setAttribute('aria-hidden', 'false');
    this._activeInput().setAttribute('aria-expanded', 'true');
  }

  _closeDropdown() {
    this._si = -1;
    this.dd.setAttribute('aria-hidden', 'true');
    this.inp.setAttribute('aria-expanded', 'false');
    if (this.mi) this.mi.setAttribute('aria-expanded', 'false');
    /* Only collapse the desktop form shell when not in mobile mode */
    if (!this._isMobile() && this.frm) this.frm.classList.remove('is-open');
  }

  _announce(msg) {
    if (this._live) this._live.textContent = msg;
  }
}

customElements.define('predictive-search', PredictiveSearch);
